import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';

// Hooks
import { usePageStability } from './hooks/usePageStability';

// Context
import { CartProvider } from './context/CartContext';

// Analytics - Initialize tracking
import './utils/analytics';
// Optional network logger (enabled via localStorage flags)
import './utils/networkDebug';
// Supabase connection diagnostics
import './utils/supabaseDiagnostics';

// Components
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Gallery from './components/Gallery';
import Shop from './components/Shop';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import QuickQuote from './components/QuickQuote';
import Footer from './components/Footer';
import AllGalleryPage from './components/AllGalleryPage';
import OnlineShop from './components/OnlineShop';
import MiniSales from './components/MiniSales';
import ProjectRebuilds from './components/ProjectRebuilds';
import Checkout from './components/Checkout';
import OrderConfirmation from './components/OrderConfirmation';
import ShoppingCart from './components/ShoppingCart';
import AuctionsPage from './components/AuctionsPage';
import MatricDance from './components/MatricDance';
import PublicDocumentViewer from './components/PublicDocumentViewer';

// Admin
const AdminApp = React.lazy(() => import('./admin/AdminApp'));

// Simple dark mode toggle
const DarkModeToggle: React.FC = () => {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [dark]);
  return (
    <button
      className="fixed top-6 right-6 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full p-2 shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center"
      onClick={() => setDark((d) => !d)}
      aria-label="Toggle dark mode"
    >
      {dark ? (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-yellow-400">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.07l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.93l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-gray-700">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z" />
        </svg>
      )}
    </button>
  );
};

function App() {
  // Initialize page stability measures to prevent tab reload issues
  const { isStabilityActive } = usePageStability();
  
  useEffect(() => {
    if (isStabilityActive) {
      console.log('🛡️ Page stability measures active - preventing tab reload issues');
    }
  }, [isStabilityActive]);

  return (
    <Router>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isViewerRoute = location.pathname.startsWith('/viewer');
  const isPlainRoute = isAdminRoute || isViewerRoute;
  const isMobileApp = Capacitor.isNativePlatform();

  // Redirect to admin if running in mobile app
  if (isMobileApp && !isAdminRoute && !isViewerRoute) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="App relative min-h-screen bg-gradient-to-br from-mini-red via-white to-mini-green overflow-x-hidden dark:bg-gray-950">
      {!isPlainRoute && <DarkModeToggle />}
      <Toaster position="top-right" />
      {!isPlainRoute && <ShoppingCart />}
      {!isPlainRoute && <Header />}
      
      <Routes>
        <Route 
          path="/" 
          element={
            <motion.main
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Hero />
              <div className="pt-8 pb-4 md:pt-16 md:pb-8">
                <About />
              </div>
              <div className="my-4 flex justify-center">
                <div style={{height: '0.5px', width: '100%', maxWidth: '120px', background: '#D1D5DB', opacity: 0.2}} />
              </div>
              <div className="pt-8 pb-4 md:pt-16 md:pb-8">
                <Services />
              </div>
              <div className="my-4 flex justify-center">
                <div style={{height: '0.5px', width: '100%', maxWidth: '120px', background: '#D1D5DB', opacity: 0.2}} />
              </div>
              <div className="pt-8 pb-4 md:pt-16 md:pb-8">
                <Gallery />
              </div>
              <div className="pt-8 pb-4 md:pt-16 md:pb-8">
                <Testimonials />
              </div>
              <div className="pt-8 pb-4 md:pt-16 md:pb-8">
                <Contact />
              </div>
            </motion.main>
          }
        />
        <Route path="/all-gallery" element={<AllGalleryPage />} />
        <Route path="/shop" element={<OnlineShop />} />
        <Route path="/mini-sales" element={<MiniSales />} />
        <Route path="/project-rebuilds" element={<ProjectRebuilds />} />
        <Route path="/auctions" element={<AuctionsPage />} />
        <Route path="/matric-dance" element={<MatricDance />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/viewer/invoice/:id" element={<PublicDocumentViewer type="Invoice" />} />
        <Route path="/viewer/quote/:id" element={<PublicDocumentViewer type="Quote" />} />
        <Route path="/admin/*" element={
          <Suspense fallback={<div>Loading admin...</div>}>
            <AdminApp />
          </Suspense>
        } />
      </Routes>
      
      {!isPlainRoute && <Footer />}
      {!isPlainRoute && <QuickQuote />}
    </div>
  );
}

export default App;
